/**
 * Validation utility functions for emails, phones, and other data validation
 */

import * as yup from 'yup';

// Define types for input and output schemas
type SchemaType<T extends yup.AnyObject> = yup.ObjectSchema<T>;
type WrappedSchema<T> = yup.ObjectSchema<{ pageToken?: string; totalCount: number; values: T[] }>;

/**
 * Simple email validation regex
 * @source Original location: UiUtils.ts.backup line 487
 */
export const SimpleEmailValidationRegExp = new RegExp('^.+@.+\\..+$', 'i');

/**
 * List of unsupported ISPs
 * @source Original location: UiUtils.ts.backup line 489
 */
export const unsupportedISPs = ['bellsouth.net', 'sbcglobal.net'];

/**
 * Checks if an ISP is supported
 * @source Original location: UiUtils.ts.backup line 491
 */
export const isSupportedISP = (isp: string) => {
	if (unsupportedISPs.includes(isp)) {
		return false;
	}

	return true;
};

/**
 * Really simple email validation. This is intentionally not robust.
 *
 * @param email Email to test
 * @source Original location: UiUtils.ts.backup line 504
 */
export const isValidEmail = (email?: string) => {
	if (email) {
		return SimpleEmailValidationRegExp.test(email);
	}

	return false;
};

/**
 * Cleans phone number by removing non-digit characters
 * @source Original location: UiUtils.ts.backup line 512
 */
export const cleanPhone = (phoneNumber: string) => {
	return phoneNumber.replace(/\D/g, '');
};

/**
 * Simple phone validation checking presence is not opted-in to text, and some additional validation if opted-in
 * @source Original location: UiUtils.ts.backup line 517
 */
export const isValidPhone = (phoneNumber: string, textOptIn?: boolean) => {
	const numbersOnlyString = cleanPhone(phoneNumber);

	if (isNaN(parseInt(numbersOnlyString, 10))) {
		return false;
	}
	if (!textOptIn) {
		return true;
	}
	const firstDigitIsOne = numbersOnlyString[0] === '1';

	if (numbersOnlyString.length === 10) {
		return true;
	} else if (firstDigitIsOne && numbersOnlyString.length === 11) {
		return true;
	}

	return false;
};

/**
 * Validates domain format
 * @source Original location: UiUtils.ts.backup line 2085
 */

export const isValidDomain = (domain: string) => {
	const split = domain.split('.');
	const subDomainChars = domain.split('.')[0];
	const topDomain = domain.substr(domain.indexOf('.') + 1);
	if (
		!domain ||
		!domain.includes('.') ||
		subDomainChars.length === 0 ||
		topDomain.length === 0 ||
		(split.length > 1 && split.filter(s => !s).length !== 0)
	) {
		return false;
	}
	return true;
};

/**
 * Function to wrap a Yup schema with a page collection schema
 * @source Original location: UiUtils.ts.backup line 819
 */
export function wrapWithPageCollectionSchema<T extends yup.AnyObject>(inputSchema: SchemaType<T>): WrappedSchema<T> {
	return yup
		.object()
		.shape({
			pageToken: yup.string().optional(),
			totalCount: yup.number().required(),
		})
		.concat(
			yup.object().shape({
				values: yup.array().of(inputSchema).optional(),
			})
		) as WrappedSchema<T>;
}

/**
 * Safe Yup validation that returns the value in production mode on error
 * @source Original location: UiUtils.ts.backup line 833
 */
export async function yupSafeValidate<T extends yup.Schema>(schema: T, value: unknown) {
	try {
		return await schema.validate(value);
	} catch (error) {
		if (import.meta.env.PROD) {
			// In production, return the value even if validation fails
			// Type assertion is safe because we're in production error mode
			return value as T['__outputType'];
		}
		throw error;
	}
}
